import { FiberNew } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { nth } from 'lodash';
import { DateInput, Form, useCreate, useNotify, useTranslate, useUpdate } from 'react-admin';
import { useQueryClient } from '@tanstack/react-query';
import useGetAvatarProperties from '@/hooks/useGetAvatarProperties';
import useGetAvatars from '@/hooks/useGetAvatars';
import { composeUri } from '@/utils/compose';
import {
    AVATAR_PROPERTIES,
    AVATARS,
    CHANGE_NODE_BY_HUMAN_ID,
    INTERVENTION_CHANGE_STATUS_NODE_LIST_OBJECT,
    PLANIFIEE_STATE_KEY,
    PLANNED_DATE_AVATAR_PROPERTY,
} from '@/utils/CONST';
import { getAvatarPropertyId, getAvatarPropertyValue } from '@/utils/getProperty';
import ButtonsBlock from '@/components/common/buttons-block/buttons-block';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import FormInputsSeparator from '@/components/common/form-inputs-separator/form-inputs-separator';

interface AddPlannedDateProps {
    open: boolean;
    onClose: () => void;
    interventionId: string;
}

const AddPlannedDate = ({ open, onClose, interventionId }: AddPlannedDateProps) => {
    const translate = useTranslate();
    const handleClose = () => {
        onClose();
    };
    const notify = useNotify();
    const [create, { isLoading: isLoadingCreate }] = useCreate();
    const [update, { isLoading: isLoadingUpdate }] = useUpdate();

    const { avatars: intervention, isLoading: isLoadingIntervention } = useGetAvatars({
        filters: {
            id: composeUri(AVATARS, interventionId),
            serialization_groups: ['read_avatar_current_node', 'read_node'],
        },
    });

    const queryClient = useQueryClient();
    const { properties, isLoading: isLoadingProperties } = useGetAvatarProperties({
        avatarId: composeUri(AVATARS, interventionId),
    });
    const planned_date = getAvatarPropertyId(properties, PLANNED_DATE_AVATAR_PROPERTY);
    const planned_date_value = getAvatarPropertyValue(properties, PLANNED_DATE_AVATAR_PROPERTY);

    const handleSubmit = (value: any) => {
        const onSuccessCallback = () => {
            create(
                CHANGE_NODE_BY_HUMAN_ID,
                {
                    data: {
                        avatar: composeUri(AVATARS, interventionId),
                        humanId: INTERVENTION_CHANGE_STATUS_NODE_LIST_OBJECT[PLANIFIEE_STATE_KEY].id,
                        extraData: [],
                    },
                    meta: {
                        api: 'v2',
                    },
                },
                {
                    onSuccess: () => {
                        // refetch();
                        handleClose();
                        queryClient.invalidateQueries({ queryKey: [AVATARS] });
                        notify('disorder.actions.change_status.one', { type: 'success' });
                    },
                },
            );
        };

        if (!!planned_date) {
            update(
                AVATAR_PROPERTIES,
                {
                    id: planned_date,
                    data: {
                        value: value[PLANNED_DATE_AVATAR_PROPERTY],
                    },
                },
                {
                    onSuccess: () => {
                        if (
                            nth(intervention)?.currentNode.configuration.idForNodeHumanReadable !==
                            INTERVENTION_CHANGE_STATUS_NODE_LIST_OBJECT[PLANIFIEE_STATE_KEY].id
                        )
                            onSuccessCallback();
                        else {
                            handleClose();
                            queryClient.invalidateQueries({ queryKey: [AVATARS] });
                            notify('disorder.actions.change_status.one', { type: 'success' });
                        }
                    },
                },
            );
        } else
            create(
                AVATAR_PROPERTIES,
                {
                    data: {
                        name: PLANNED_DATE_AVATAR_PROPERTY,
                        value: value[PLANNED_DATE_AVATAR_PROPERTY],
                        avatar: composeUri(AVATARS, interventionId),
                    },
                },
                {
                    onSuccess: () => {
                        onSuccessCallback();
                    },
                },
            );
    };

    return (
        <DialogTemplate
            icon={<FiberNew />}
            title={translate('intervention.list.planned_date')}
            open={open}
            onClose={handleClose}
        >
            <Form onSubmit={handleSubmit} defaultValues={{ [PLANNED_DATE_AVATAR_PROPERTY]: planned_date_value }}>
                <Box display={'grid'} gap={1} data-cy='planned-date-form'>
                    <FormInputsSeparator>
                        <DateInput
                            data-cy='planned-date-input'
                            label={'intervention.list.planned_date'}
                            helperText={false}
                            name={PLANNED_DATE_AVATAR_PROPERTY}
                            source={PLANNED_DATE_AVATAR_PROPERTY}
                        />
                    </FormInputsSeparator>

                    <ButtonsBlock>
                        <Button
                            onClick={handleClose}
                            disabled={
                                isLoadingCreate || isLoadingUpdate || isLoadingProperties || isLoadingIntervention
                            }
                        >
                            {translate('ra.action.back')}
                        </Button>

                        <LoadingButton
                            data-cy='submit-planned-date'
                            loading={isLoadingCreate || isLoadingUpdate || isLoadingProperties || isLoadingIntervention}
                            type={'submit'}
                            variant={'contained'}
                        >
                            {translate('ra.action.edit')}
                        </LoadingButton>
                    </ButtonsBlock>
                </Box>
            </Form>
        </DialogTemplate>
    );
};

export default AddPlannedDate;
